import { useEffect } from 'react';
import { v4 } from 'uuid';
import { BASE_SHEET_WIDTH, CANVAS_PIXEL_RATIO, DEFAULT_LINE_WIDTH_IN_EM } from '@i-ark/constants';
import '@i-ark/models';
import '@utils/component-framework';
import { useCreateLineEntryMutation } from '../../../state/mutations/entries/line.create.js';
import '@tanstack/react-query';
import { iArkStore } from '../../../state/store.js';

const SNAP_TO_ANGLE = Math.PI / 12;
const useDrawHandler = (pageId, ref) => {
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const dispatch = iArkStore.useDispatch();
  const createLineEntryMutation = useCreateLineEntryMutation(entryKey);
  const zoom = iArkStore.useSelector(state => state.zoom);
  const lineColor = iArkStore.useSelector(state => state.lineColor);
  useEffect(() => {
    const canvas = ref.current;
    const canvasCtx = canvas?.getContext("2d");
    if (canvas === null || canvasCtx == null) {
      return;
    }
    let canvasBox;
    let scale;
    let initialX;
    let initialY;
    let snapToGrid = false;

    // helper to calculate current coordinates based on whether or not we're
    // currently snapping to grid
    const getCurrCoords = evt => {
      const x = Math.max(0, Math.min(1, (evt.pageX - window.pageXOffset - canvasBox.left) / canvasBox.width));
      const y = Math.max(0, Math.min(1, (evt.pageY - window.pageYOffset - canvasBox.top) / canvasBox.height));
      if (!snapToGrid) {
        return {
          x,
          y
        };
      }
      const minX = Math.min(initialX, x);
      const minY = Math.min(initialY, y);
      const maxX = Math.max(initialX, x);
      const maxY = Math.max(initialY, y);
      const width = maxX - minX;
      const height = maxY - minY;
      const rawAngle = Math.atan2(canvasBox.height * (y - initialY), canvasBox.width * (x - initialX));
      const snappedAngle = Math.round(rawAngle / SNAP_TO_ANGLE) * SNAP_TO_ANGLE;
      const length = Math.sqrt((width * canvasBox.width) ** 2 + (height * canvasBox.height) ** 2);
      let snapX = length * Math.cos(snappedAngle) / canvasBox.width + initialX;
      let snapY = length * Math.sin(snappedAngle) / canvasBox.height + initialY;

      // ensure that the line cannot exceed the bounds of the canvas
      let lengthOverflowX = 0;
      let lengthOverflowY = 0;
      if (snapX < 0) {
        lengthOverflowX = -snapX / initialX;
      } else if (snapX > 1) {
        lengthOverflowX = (snapX - 1) / (1 - initialX);
      }
      if (snapY < 0) {
        lengthOverflowY = -snapY / initialY;
      } else if (snapY > 1) {
        lengthOverflowY = (snapY - 1) / (1 - initialY);
      }
      const lengthOverflow = Math.max(0, lengthOverflowX, lengthOverflowY);
      if (lengthOverflow > 0) {
        snapX = length * (1 - lengthOverflow) * Math.cos(snappedAngle) / canvasBox.width + initialX;
        snapY = length * (1 - lengthOverflow) * Math.sin(snappedAngle) / canvasBox.height + initialY;
      }
      return {
        x: snapX,
        y: snapY
      };
    };
    const onPointerDown = evt => {
      if (evt.button !== 0) {
        return;
      }
      canvasBox = canvas.getBoundingClientRect();
      scale = canvasBox.width / BASE_SHEET_WIDTH;
      initialX = Math.max(0, Math.min(1, (evt.pageX - window.pageXOffset - canvasBox.left) / canvasBox.width));
      initialY = Math.max(0, Math.min(1, (evt.pageY - window.pageYOffset - canvasBox.top) / canvasBox.height));

      // initialize canvas for high-res drawing
      canvas.width = canvasBox.width * CANVAS_PIXEL_RATIO;
      canvas.height = canvasBox.height * CANVAS_PIXEL_RATIO;
      window.addEventListener("pointermove", onPointerMove, {
        passive: false
      });
      window.addEventListener("pointerup", onPointerUp, {
        passive: false
      });
      window.addEventListener("touchmove", preventDefault, {
        passive: false
      });
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);
      evt.preventDefault();
    };
    const onPointerMove = evt => {
      const {
        x,
        y
      } = getCurrCoords(evt);
      canvasCtx.clearRect(0, 0, canvasBox.width * CANVAS_PIXEL_RATIO, canvasBox.height * CANVAS_PIXEL_RATIO);
      canvasCtx.beginPath();
      canvasCtx.moveTo(initialX * canvasBox.width * CANVAS_PIXEL_RATIO, initialY * canvasBox.height * CANVAS_PIXEL_RATIO);
      canvasCtx.lineTo(x * canvasBox.width * CANVAS_PIXEL_RATIO, y * canvasBox.height * CANVAS_PIXEL_RATIO);
      canvasCtx.strokeStyle = lineColor;
      canvasCtx.lineWidth = Math.max(1, Math.round(DEFAULT_LINE_WIDTH_IN_EM * scale * CANVAS_PIXEL_RATIO / zoom));
      canvasCtx.stroke();
      evt.preventDefault();
    };
    const onPointerUp = evt => {
      const {
        x,
        y
      } = getCurrCoords(evt);
      const minX = Math.min(initialX, x);
      const minY = Math.min(initialY, y);
      const maxX = Math.max(initialX, x);
      const maxY = Math.max(initialY, y);

      // we reverse the direction of the line, if the user hasn't drawn in a
      // natural ltr direction (ie. top-to-bottom + left-to-right OR
      // bottom-to-top + right-to-left)
      const reverseLineDirection = initialX <= x !== initialY <= y;
      const lineId = v4();
      dispatch(iArkStore.actions.lineInserted());
      dispatch(iArkStore.actions.selectItem(lineId));
      createLineEntryMutation({
        pageId,
        lineId,
        position: {
          x: minX,
          y: minY
        },
        size: {
          width: maxX - minX,
          height: maxY - minY
        },
        reverseLineDirection,
        style: {
          stroke: {
            width: DEFAULT_LINE_WIDTH_IN_EM,
            color: lineColor
          }
        }
      });
      removeEventListeners();
      evt.preventDefault();
    };
    const onKeyDown = evt => {
      switch (evt.key) {
        case "Shift":
          snapToGrid = true;
          break;
        case "Escape":
          canvasCtx.clearRect(0, 0, canvasBox.width * CANVAS_PIXEL_RATIO, canvasBox.height * CANVAS_PIXEL_RATIO);
          removeEventListeners();
          dispatch(iArkStore.actions.toggleLineTool({
            enabled: false
          }));
          break;
      }
    };
    const onKeyUp = evt => {
      switch (evt.key) {
        case "Shift":
          snapToGrid = false;
          break;
      }
    };
    const preventDefault = evt => {
      evt.preventDefault();
    };
    const removeEventListeners = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", preventDefault);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    canvas.addEventListener("pointerdown", onPointerDown, {
      passive: false
    });
    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, lineColor]);
};

export { useDrawHandler };
//# sourceMappingURL=hook.draw-handler.js.map
